/**
 * AppDownloads.tsx — "Install the apps": the download link for the desktop
 * client, so it lives in Settings instead of a GitHub Actions artifact.
 *
 * VISIBLE TO ALL STAFF (Andrew 2026-10-27): unlike Users/Lines/Aliases this
 * pane has NO admin guard — the installer is served publicly under /updates/
 * anyway, and any tech should be able to grab the link to send a client.
 *
 * Reads the SAME per-platform `latest.json` the self-updater reads
 * (`/updates/<platform>/latest.json`, served read-only behind the tunnel's
 * /updates/ rule — same origin as the console). The manifest already carries
 * the published version and the absolute installer URL, so this shows the
 * current version and a direct Download link, or "No build published yet" when
 * nothing has been published. Structured as a per-platform list so macOS slots
 * in with one more PLATFORMS entry and no new plumbing.
 */
import { useEffect, useState } from 'react'

interface UpdateManifest {
  version?: string
  platforms?: Record<string, { url?: string } | undefined>
}

/** Add { key: 'darwin-aarch64', label: 'macOS (Apple silicon)' } etc. when those publish. */
const PLATFORMS: ReadonlyArray<{ key: string; label: string }> = [{ key: 'windows-x86_64', label: 'Windows' }]

type RowState =
  | { status: 'loading' }
  | { status: 'ready'; version: string; url: string }
  | { status: 'none' }
  | { status: 'error' }

function useManifest(platformKey: string): RowState {
  const [state, setState] = useState<RowState>({ status: 'loading' })
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // no-store: the point of this pane is to show the CURRENT published
        // build, never a cached older manifest.
        const res = await fetch(`/updates/${platformKey}/latest.json`, { cache: 'no-store' })
        if (res.status === 404) {
          if (!cancelled) setState({ status: 'none' })
          return
        }
        if (!res.ok) {
          if (!cancelled) setState({ status: 'error' })
          return
        }
        const manifest = (await res.json()) as UpdateManifest
        const url = manifest.platforms?.[platformKey]?.url
        if (!manifest.version || !url) {
          if (!cancelled) setState({ status: 'none' })
          return
        }
        if (!cancelled) setState({ status: 'ready', version: manifest.version, url })
      } catch {
        if (!cancelled) setState({ status: 'error' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [platformKey])
  return state
}

function DownloadRow({ platformKey, label }: { platformKey: string; label: string }) {
  const state = useManifest(platformKey)
  return (
    <div
      data-testid={`app-download-${platformKey}`}
      className="flex items-center justify-between gap-3 rounded-[8px] border border-subtle bg-card px-3 py-2.5"
    >
      <div className="flex flex-col">
        <span className="text-[13px] font-medium text-hi">{label}</span>
        <span className="text-[12px] text-mid">
          {state.status === 'loading'
            ? 'Checking for the latest build…'
            : state.status === 'ready'
              ? `Version ${state.version}`
              : state.status === 'none'
                ? 'No build published yet'
                : "Couldn't check right now"}
        </span>
      </div>
      {state.status === 'ready' ? (
        <a
          data-testid={`app-download-link-${platformKey}`}
          href={state.url}
          className="inline-flex items-center rounded-control bg-brand-primary-strong px-3 py-1.5 text-[13px] font-medium text-on-brand hover:bg-cc-blue-dim"
        >
          Download
        </a>
      ) : null}
    </div>
  )
}

export function AppDownloads() {
  return (
    <div data-testid="app-downloads" className="flex flex-col gap-3">
      <p className="text-[13px] text-mid">
        Install the desktop client. These links are public — send them straight to anyone who needs the app.
      </p>
      <div className="flex flex-col gap-2">
        {PLATFORMS.map((p) => (
          <DownloadRow key={p.key} platformKey={p.key} label={p.label} />
        ))}
      </div>
    </div>
  )
}
